import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Textarea } from '@freelancer-os/ui/src/components/ui/textarea';
import type { ChatAttachment } from '@freelancer-os/shared-types';
import { AlertCircle, FileText, Image as ImageIcon, Paperclip, Send, X } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { UploadingFile } from '../../../hooks/useFileUpload';

export interface ChatInputHandle {
  focus: () => void;
}

interface ChatInputProps {
  value: string;
  onValueChange: (value: string) => void;
  onSend: (message: string, attachments?: ChatAttachment[]) => void;
  disabled?: boolean;
  files: UploadingFile[];
  pendingFiles: UploadingFile[];
  isUploading: boolean;
  addFiles: (fileList: FileList | File[]) => void;
  removeFile: (id: string) => void;
  clear: () => void;
  showFileHint?: boolean;
  onDismissFileHint?: () => void;
}

function FileIcon({ fileType }: { fileType: string }) {
  if (fileType.startsWith('image/')) {
    return <ImageIcon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />;
  }
  return <FileText className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />;
}

function FileChip({ file, onRemove }: { file: UploadingFile; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-100 border border-surface-200 rounded-md text-xs">
      {file.status === 'uploading' && (
        <div className="w-3.5 h-3.5 border-2 border-surface-500 border-t-transparent rounded-full animate-spin shrink-0" />
      )}
      {file.status === 'done' && <FileIcon fileType={file.fileType} />}
      {file.status === 'error' && (
        <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" strokeWidth={1.8} />
      )}
      <span className="truncate max-w-[160px] font-medium" title={file.error ?? file.fileName}>
        {file.fileName}
      </span>
      {file.status === 'uploading' && (
        <span className="font-mono text-[10px] text-surface-500 shrink-0">{file.progress}%</span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="text-surface-400 hover:text-surface-700 shrink-0"
        aria-label="Remove file"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  {
    value,
    onValueChange,
    onSend,
    disabled,
    files,
    pendingFiles,
    isUploading,
    addFiles,
    removeFile,
    clear,
    showFileHint,
    onDismissFileHint,
  },
  ref,
) {
  const { t } = useTranslation('chat');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
  }));

  // Resize textarea when value changes from outside (e.g. SuggestionGrid pick).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [value]);

  const handleSend = () => {
    const trimmed = value.trim();
    if ((!trimmed && pendingFiles.length === 0) || disabled || isUploading) return;

    const attachments: ChatAttachment[] | undefined =
      pendingFiles.length > 0
        ? pendingFiles.map((f) => ({
            documentId: f.documentId!,
            fileName: f.fileName,
            fileType: f.fileType,
            fileUrl: f.fileUrl!,
          }))
        : undefined;

    onSend(trimmed, attachments);
    onValueChange('');
    clear();
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = ''; // allow re-selecting the same file
    }
  };

  const sendDisabled = disabled || isUploading || (!value.trim() && pendingFiles.length === 0);

  return (
    <div className="px-5 pb-5 pt-2 shrink-0">
      <div className="max-w-[720px] mx-auto">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="application/pdf,text/csv,image/jpeg,image/png,image/webp,image/heic,image/heif"
          className="hidden"
          onChange={handleFileSelect}
        />

        {showFileHint && files.length === 0 && (
          <div className="mb-2 inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-amber-50 border border-amber-200 rounded text-[12px] font-medium text-amber-700 animate-[fade-up_0.25s_ease-out]">
            <Paperclip className="w-3.5 h-3.5" strokeWidth={2} />
            {t('input.fileHint')}
            <button
              type="button"
              onClick={onDismissFileHint}
              className="text-amber-600 hover:text-amber-800 shrink-0 ml-0.5"
              aria-label={t('input.dismissFileHint')}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {files.map((file) => (
              <FileChip key={file.id} file={file} onRemove={() => removeFile(file.id)} />
            ))}
          </div>
        )}

        <div className="flex items-end gap-3 bg-surface-0 border border-surface-200 px-4 py-3 transition-shadow duration-200 focus-within:shadow-[0_0_0_2px_rgba(22,163,74,0.2)]">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-surface-400 hover:text-surface-600 shrink-0 mb-0.5"
            title={t('input.attach')}
            onClick={openFilePicker}
            type="button"
          >
            <Paperclip className="w-5 h-5" strokeWidth={1.8} />
          </Button>
          <div className="flex-1 min-w-0">
            <Textarea
              ref={textareaRef}
              rows={1}
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onInput={handleInput}
              placeholder={t('input.placeholder')}
              className="min-h-0 w-full resize-none border-0 shadow-none rounded-none p-0 text-[14px] text-surface-800 placeholder-surface-400 bg-transparent leading-relaxed max-h-[120px] focus-visible:ring-0"
              disabled={disabled}
            />
          </div>
          <Button
            size="icon"
            onClick={handleSend}
            disabled={sendDisabled}
            className="w-9 h-9 bg-brand-500 hover:bg-brand-600 disabled:opacity-40 disabled:hover:bg-brand-500 text-white shrink-0"
          >
            <Send className="w-4 h-4" strokeWidth={2.5} />
          </Button>
        </div>
        <div className="flex items-center justify-center mt-2 gap-4">
          <span className="text-[11px] text-surface-400">
            <kbd className="px-1.5 py-0.5 bg-surface-100 border border-surface-200 font-mono text-[10px]">
              Enter
            </kbd>{' '}
            {t('input.enterToSend')} ·{' '}
            <kbd className="px-1.5 py-0.5 bg-surface-100 border border-surface-200 font-mono text-[10px]">
              Shift+Enter
            </kbd>{' '}
            {t('input.shiftEnterNewline')}
          </span>
        </div>
      </div>
    </div>
  );
});
